import React, { useEffect, useMemo, useState } from "react";
import { executeMutation, executeQuery } from "../../../graphql/client";

// Season Pass tab (epic #307 phase 1): who bought a one-time Season
// Pass, whether it is still live, and jersey fulfillment (the pass
// includes a shirt). Reads the admin-only seasonPassHolders query;
// the only write is the shirt-given toggle (legacy `shirtgiven`
// column). Sales themselves are configured outside the app: a
// one-time Price in Stripe + STRIPE_SEASON_PASS_PRICE_ID on the
// graphql Lambda — the header says whether that is set.

const OCEAN = "var(--ab-ocean, #0e7c9b)";
const SEAFOAM = "var(--ab-seafoam, #e7f4f3)";

const HOLDERS_QUERY = `query SeasonPassHolders {
  seasonPassHolders {
    userId name email sex until purchasedAt active paidMember shirtSize shirtGiven
  }
  mySeasonPass { available priceCents currency endsOn }
}`;

const SET_SHIRT = `mutation SetShirtGiven($userId: ID!, $given: Boolean!) {
  setShirtGiven(userId: $userId, given: $given) { userId shirtGiven }
}`;

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const fmtPrice = (cents, currency) => {
  if (cents == null) return "";
  const amt = (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
  return `$${amt}${currency && currency !== "usd" ? " " + currency.toUpperCase() : ""}`;
};

const card = {
  border: "1px solid #e6e9f0",
  borderRadius: 10,
  background: "#fff",
  padding: "12px 14px",
  marginBottom: 14,
};
const muted = { color: "#6a7385", fontSize: 13 };
const th = {
  textAlign: "left",
  fontSize: 12,
  color: "#6a7385",
  fontWeight: 600,
  padding: "4px 6px",
  borderBottom: "1px solid #e6e9f0",
  whiteSpace: "nowrap",
};
const td = { padding: "6px", fontSize: 14, verticalAlign: "top" };

const pill = (active) => ({
  border: active ? `2px solid ${OCEAN}` : "1px solid #d7dbe4",
  background: active ? SEAFOAM : "#fff",
  color: "#333",
  borderRadius: 999,
  padding: "6px 14px",
  fontSize: 13,
  fontWeight: active ? 700 : 500,
  cursor: "pointer",
});

const Stat = ({ label, value, sub }) => (
  <div style={{ ...card, marginBottom: 0, flex: "1 1 140px", padding: "10px 12px" }}>
    <div style={{ fontSize: 12, color: "#6a7385", fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color: "#1f2330" }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#8a93a3" }}>{sub}</div>}
  </div>
);

const FILTERS = [
  { id: "active", label: "Live passes" },
  { id: "shirt", label: "Owed a shirt" },
  { id: "all", label: "Everyone" },
];

const SeasonPassTab = () => {
  const [holders, setHolders] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("active");
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const out = await executeQuery(HOLDERS_QUERY);
      setHolders(out?.seasonPassHolders || []);
      setConfig(out?.mySeasonPass || null);
    } catch (err) {
      setError(err.message || "Couldn't load Season Pass holders.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const liveCount = holders.filter((h) => h.active).length;
  const owedShirt = holders.filter((h) => !h.shirtGiven).length;

  const filtered = useMemo(() => {
    if (filter === "active") return holders.filter((h) => h.active);
    if (filter === "shirt") return holders.filter((h) => !h.shirtGiven);
    return holders;
  }, [holders, filter]);

  const emails = useMemo(
    () => [...new Set(filtered.map((h) => h.email).filter(Boolean))],
    [filtered]
  );

  const copyEmails = async () => {
    try {
      await navigator.clipboard.writeText(emails.join(", "));
      setCopied(`Copied ${emails.length} email${emails.length === 1 ? "" : "s"}`);
    } catch (err) {
      setCopied("Couldn't copy — select the list instead");
    }
    setTimeout(() => setCopied(""), 2500);
  };

  const toggleShirt = async (h) => {
    setBusy(h.userId);
    setError("");
    try {
      await executeMutation(SET_SHIRT, {
        userId: h.userId,
        given: !h.shirtGiven,
      });
      setHolders((prev) =>
        prev.map((x) =>
          x.userId === h.userId ? { ...x, shirtGiven: !h.shirtGiven } : x
        )
      );
    } catch (err) {
      setError(err.message || "Couldn't update the shirt flag.");
    } finally {
      setBusy("");
    }
  };

  const onSale = config?.available === true;

  return (
    <div style={{ maxWidth: 900 }}>
      <h2 style={{ marginTop: 0 }}>
        <span role="img" aria-label="ticket">🎫</span> Season Pass
      </h2>
      <p style={muted}>
        One-time passes bought through Stripe Checkout. A live pass gives
        member standing until its end date, then access lapses on its own
        (members get the next season free, which is the upsell). Jersey
        column tracks the shirt that comes with the pass.
      </p>

      <div
        style={{
          ...card,
          background: onSale ? SEAFOAM : "#fff8e6",
          borderColor: onSale ? "#bfe0dc" : "#f1d9a0",
        }}
      >
        {onSale ? (
          <span style={{ fontSize: 14 }}>
            <strong>On sale</strong>
            {config.priceCents != null && (
              <> at {fmtPrice(config.priceCents, config.currency)}</>
            )}
            {config.endsOn && (
              <>
                {" "}
                · a pass bought today runs through{" "}
                <strong>{fmtDate(config.endsOn)}</strong>
              </>
            )}
            . Members see a Season Pass button next to Subscribe.
          </span>
        ) : (
          <span style={{ fontSize: 14 }}>
            <strong>Not on sale.</strong> To open sales: create a one-time
            Price in the Stripe Dashboard, then set{" "}
            <code>StripeSeasonPassPriceId</code> (and optionally{" "}
            <code>SeasonPassEndsOn</code> = season end date) on the graphql
            stack. Until then nothing new shows to members.
          </span>
        )}
      </div>

      {error && (
        <div
          style={{
            background: "#fdecea",
            border: "1px solid #e7b3ba",
            color: "#b00020",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <Stat label="Live passes" value={liveCount} />
        <Stat label="Ever bought" value={holders.length} />
        <Stat label="Owed a shirt" value={owedShirt} sub="pass holders without shirtgiven" />
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            style={pill(filter === f.id)}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={copyEmails}
          disabled={!emails.length}
          style={{ ...pill(false), opacity: emails.length ? 1 : 0.5 }}
        >
          Copy {emails.length} email{emails.length === 1 ? "" : "s"}
        </button>
        <button type="button" onClick={load} style={pill(false)}>
          Refresh
        </button>
        {copied && <span style={muted}>{copied}</span>}
      </div>

      {loading && !holders.length && <p style={muted}>Loading…</p>}

      {!loading && filtered.length === 0 && (
        <p style={muted}>
          {holders.length === 0
            ? "Nobody has bought a Season Pass yet."
            : "Nobody matches this filter."}
        </p>
      )}

      {filtered.length > 0 && (
        <div style={{ ...card, overflowX: "auto", padding: 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Name</th>
                <th style={th}>Email</th>
                <th style={th}>Pass</th>
                <th style={th}>Through</th>
                <th style={th}>Bought</th>
                <th style={th}>Shirt</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((h) => (
                <tr key={h.userId} style={{ borderBottom: "1px solid #f0f2f6" }}>
                  <td style={td}>
                    {h.name || <span style={muted}>(no name)</span>}
                    {h.paidMember && (
                      <span
                        title="Also a live monthly subscriber"
                        style={{ ...muted, marginLeft: 6 }}
                        role="img"
                        aria-label="also a monthly subscriber"
                      >
                        💳
                      </span>
                    )}
                  </td>
                  <td style={{ ...td, wordBreak: "break-all" }}>{h.email || ""}</td>
                  <td style={td}>
                    <span
                      style={{
                        background: h.active ? "#e4f5e8" : "#f0f2f6",
                        color: h.active ? "#1f7a3a" : "#6a7385",
                        borderRadius: 999,
                        padding: "2px 9px",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {h.active ? "live" : "ended"}
                    </span>
                  </td>
                  <td style={td}>{fmtDate(h.until)}</td>
                  <td style={td}>{fmtDate(h.purchasedAt)}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    <span style={{ marginRight: 8 }}>
                      {h.shirtSize ? String(h.shirtSize).toUpperCase() : "—"}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleShirt(h)}
                      disabled={busy === h.userId}
                      style={{
                        ...pill(h.shirtGiven),
                        padding: "3px 10px",
                        fontSize: 12,
                      }}
                    >
                      {h.shirtGiven ? "✓ given" : "mark given"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SeasonPassTab;
